// The server owns operations. Cancelling this observer never cancels provisioning.
export const STATUS_POLL_MS = 10000;
export const BUSY_STATES = ["queued", "running", "waiting", "requires_review"];

export function mailboxState(email) {
    const op = email.operation;
    if (op && BUSY_STATES.includes(op.state)) {
        if (op.state === "requires_review") return "requires_review";
        if (op.state === "queued" || op.state === "waiting") return "waiting";
        return op.action === "delete" ? "deleting" : "pending";
    }
    if (op?.state === "failed") return "failed";
    return email.stack_status === "active" ? "active" : email.stack_status === "pending" ? "checking" : "failed";
}

export class MailboxCreation {
    constructor({api, channelId, senderEmail, onUpdate, onEmails, operation, action = "create", emailId}) {
        Object.assign(this, {api, channelId, senderEmail, onUpdate, onEmails, operation, action, emailId});
        this.running = false;
    }

    cancel() {
        this.running = false;
        this.disposed = true;
        clearTimeout(this.nextPoll);
    }

    update(operation) {
        this.operation = operation;
        const phase = operation.state === "succeeded" ? (operation.action === "delete" ? "deleted" : "success")
            : operation.state === "requires_review" ? "requires_review"
            : operation.state === "failed" ? "failed"
            : ["queued", "waiting"].includes(operation.state) ? "waiting"
            : operation.action === "delete" ? "deleting" : "pending";
        this.onUpdate({phase, senderEmail: operation.sender_email, operation,
            errorCode: operation.error_code, detail: operation.error_detail});
        if (["succeeded", "failed", "requires_review"].includes(operation.state)) {
            this.running = false;
            clearTimeout(this.nextPoll);
        }
    }

    async start(submit = true) {
        if (this.running) return;
        this.running = true;
        this.disposed = false;
        if (submit) {
            try {
                const operation = this.action === "retry"
                    ? await this.api.retry_channel_email(this.channelId, this.emailId)
                    : await this.api.add_channel_email(this.channelId, {sender_email: this.senderEmail});
                if (!this.running) return;
                this.update(operation);
            } catch (error) {
                if (!this.running) return;
                this.cancel();
                this.onUpdate({phase: [400, 403, 404, 409, 422].includes(error?.status) ? "failed" : "unconfirmed", senderEmail: this.senderEmail,
                    detail: error?.responseJSON?.error, errorCode: error?.responseJSON?.code});
                return; // Never automatically repeat a POST after a lost reply.
            }
        } else if (this.operation) {
            this.update(this.operation);
        }
        if (this.running) await this.poll();
    }

    async poll() {
        try {
            const operation = await this.api.get_mailbox_operation(this.channelId, this.operation.id);
            if (!this.running) return;
            this.update(operation);
            const emails = await this.api.get_channel_emails(this.channelId);
            if (!this.disposed) this.onEmails(emails || []);
        } catch (error) {
            if (!this.running) return;
            if ([401, 403, 404].includes(error?.status)) {
                this.cancel();
                this.onUpdate({phase: "unconfirmed", senderEmail: this.senderEmail});
                return;
            }
        }
        if (this.running) this.nextPoll = setTimeout(() => this.poll(), STATUS_POLL_MS);
    }
}
